import axios from 'axios';

// Base URL for the backend API
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api/devices`,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Function to set auth token for requests
export const setAuthToken = (token) => {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
  }
};

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const data = error.response.data;
      
      console.error('API Error Response:', {
        status,
        data,
        url: error.config?.url
      });
      
      let message = 'An error occurred';

      // If backend returns structured error, use that message
      if (data && typeof data === 'object') {
        if (data.message) {
          message = data.message;
        } else if (data.error) {
          message = data.error;
        } else if (data.details) {
          // Validation errors
          message = 'Validation failed: ' + JSON.stringify(data.details);
        }
      } else {
        // Fallback to status-based messages
        switch (status) {
          case 400:
            message = 'Invalid request. Please check your input.';
            break;
          case 404:
            message = 'Device not found.';
            break;
          case 500:
            message = 'Server error. Please try again later.';
            break;
          default:
            message = `Server error (${status}). Please try again.`;
        }
      }

      throw new Error(message);
    } else if (error.request) {
      // Network error
      throw new Error('Network error. Please check your connection.');
    } else {
      throw new Error('An unexpected error occurred.');
    }
  }
);

// Device API service
export const deviceService = {
  // GET requests

  // Get all devices
  getAllDevices: async (homeId = 1) => {
    try {
      const response = await apiClient.get('', {
        params: { homeId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching all devices:', error);
      throw error;
    }
  },

  // Get devices by room ID
  getDevicesByRoom: async (roomId) => {
    try {
      const response = await apiClient.get('', {
        params: { roomId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching devices by room:', error);
      throw error;
    }
  },

  // Get devices by home ID
  getDevicesByHome: async (homeId) => {
    try {
      const response = await apiClient.get('', {
        params: { homeId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching devices by home:', error);
      throw error;
    }
  },

  // Search devices by name
  searchDevices: async (searchTerm) => {
    try {
      const response = await apiClient.get('', {
        params: { search: searchTerm }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching devices:', error);
      throw error;
    }
  },

  // Get device by ID
  getDeviceById: async (deviceId) => {
    try {
      const response = await apiClient.get(`/${deviceId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching device by ID:', error);
      throw error;
    }
  },

  // POST requests

  // Create a new device
  createDevice: async (deviceData) => {
    try {
      const response = await apiClient.post('', deviceData);
      return response.data;
    } catch (error) {
      console.error('Error creating device:', error);
      throw error;
    }
  },

  // Turn device on
  turnDeviceOn: async (deviceId) => {
    try {
      const response = await apiClient.post(`/${deviceId}/on`);
      return response.data;
    } catch (error) {
      console.error('Error turning device on:', error);
      throw error;
    }
  },

  // Turn device off
  turnDeviceOff: async (deviceId) => {
    try {
      const response = await apiClient.post(`/${deviceId}/off`);
      return response.data;
    } catch (error) {
      console.error('Error turning device off:', error);
      throw error;
    }
  },

  // PUT requests

  // Control device (generic control method)
  controlDevice: async (deviceId, controlData) => {
    try {
      const response = await apiClient.put(`/${deviceId}/control`, controlData);
      return response.data;
    } catch (error) {
      console.error('Error controlling device:', error);
      throw error;
    }
  },

  // Update device brightness (for lights)
  updateBrightness: async (deviceId, brightness) => {
    try {
      const payload = {
        action: 'brightness',
        value: brightness
      };
      const response = await apiClient.put(`/${deviceId}/control`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating brightness:', error);
      throw error;
    }
  },

  // Update thermostat setpoint (for thermostats)
  updateSetpoint: async (deviceId, setpoint) => {
    try {
      const payload = {
        action: 'temperature',
        value: setpoint
      };
      const response = await apiClient.put(`/${deviceId}/control`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating setpoint:', error);
      throw error;
    }
  },

  // DELETE requests

  // Delete device
  deleteDevice: async (deviceId) => {
    try {
      await apiClient.delete(`/${deviceId}`);
      return true;
    } catch (error) {
      console.error('Error deleting device:', error);
      throw error;
    }
  },
};

export default deviceService;